import { Component, Prop, Vue } from 'vue-property-decorator';

import { RewardRequest, RewardRequestDTO } from '@/models';
import { NOTIFICATION, PRIZE_TYPES, REWARD_REQUEST_STATUSES } from '@/constants';
import { updateRewardRequest, userApproveRewardRequest } from '@/services';

import ExchangeItem from '@/components/pages/rewards/reward-history/exchange-item/ExchangeItem.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import RequestApproveDialog from './request-approve-dialog/RequestApproveDialog.vue';

enum TabItem {
  PROCESSING = 'Processing',
  HISTORY = 'History'
}

@Component({
  components: {
    ExchangeItem,
    InlineLoader,
    ConfirmationDialog,
    RequestApproveDialog
  }
})
export default class RewardsHistory extends Vue {
  @Prop({ default: [] as RewardRequestDTO[] }) private exchangeRewardsHistory: RewardRequestDTO[];
  @Prop({ default: true }) private isFetchingHistory: boolean;

  private tab = null;
  private readonly tabItems = [TabItem.PROCESSING, TabItem.HISTORY];
  private confirmCancelDialogShown = false;
  private confirmApproveDialogShown = false;
  private requestItem = {} as RewardRequestDTO;

  getDataSourceForTab(tab: string): RewardRequestDTO[] {
    let filterExchanges: RewardRequestDTO[] = [];
    if (tab === TabItem.PROCESSING) {
      filterExchanges = this.exchangeRewardsHistory.filter(
        exchange =>
          exchange.status === REWARD_REQUEST_STATUSES.PENDING.label ||
          exchange.status === REWARD_REQUEST_STATUSES.IN_PROGRESS.label
      );
    } else {
      filterExchanges = this.exchangeRewardsHistory.filter(
        exchange =>
          exchange.status !== REWARD_REQUEST_STATUSES.PENDING.label &&
          exchange.status !== REWARD_REQUEST_STATUSES.IN_PROGRESS.label
      );
    }
    return filterExchanges;
  }

  isProcessing(item: string): boolean {
    return item === TabItem.PROCESSING;
  }

  showCancelDialog(item: RewardRequestDTO): void {
    this.requestItem = item;
    this.confirmCancelDialogShown = true;
  }

  async cancelRequest(): Promise<void> {
    const data: RewardRequest[] = [
      {
        id: this.requestItem.id,
        userId: this.requestItem.user.id,
        status: 'REJECTED',
        reason: 'Canceled by user',
        rewardItemId: this.requestItem.rewardItem.id
      }
    ];
    const updateRewardRequestResult = await updateRewardRequest(data);
    const notificationData = {
      showing: true,
      type: '',
      title: '',
      content: [],
      icon: '',
      multiline: false,
      height: 0
    };
    if (updateRewardRequestResult.data) {
      notificationData.type = NOTIFICATION.TYPE.success;
      notificationData.title = NOTIFICATION.TEXT.cancelRewardRequestSuccess;
      notificationData.icon = NOTIFICATION.ICON.success;
      this.$emit('onRefetchData', true);
    } else {
      notificationData.type = NOTIFICATION.TYPE.error;
      notificationData.title = NOTIFICATION.TEXT.cancelRewardRequestFailed;
      notificationData.icon = NOTIFICATION.ICON.error;
    }
    this.$store.commit('notification/setNotification', notificationData);
  }

  onCancelDialogConfirmed(): void {
    this.cancelRequest();
    this.confirmCancelDialogShown = false;
  }

  async approveRequest(invoiceImage: File | null): Promise<void> {
    const data: RewardRequest = {
      id: this.requestItem.id,
      userId: this.requestItem.user.id,
      status: REWARD_REQUEST_STATUSES.USER_APPROVED.label,
      rewardItemId: this.requestItem.rewardItem.id,
      invoiceImage
    };
    const updateRewardRequestResult = await userApproveRewardRequest(data);
    const notificationData = {
      showing: true,
      type: '',
      title: '',
      content: [],
      icon: '',
      multiline: false,
      height: 0
    };
    if (updateRewardRequestResult.data) {
      notificationData.type = NOTIFICATION.TYPE.success;
      notificationData.title = NOTIFICATION.TEXT.approveRewardRequestSuccess;
      notificationData.icon = NOTIFICATION.ICON.success;
      this.$emit('onRefetchData', true);
    } else {
      notificationData.type = NOTIFICATION.TYPE.error;
      notificationData.title = NOTIFICATION.TEXT.approveRewardRequestFailed;
      notificationData.icon = NOTIFICATION.ICON.error;
    }
    this.$store.commit('notification/setNotification', notificationData);
  }

  showApproveDialog(item: RewardRequestDTO): void {
    this.requestItem = item;
    if (item.rewardItem.prize.type === PRIZE_TYPES.VOUCHER) {
      this.approveRequest(null);
    } else {
      this.confirmApproveDialogShown = true;
    }
  }

  onApproveDialogConfirmed(invoiceImage: File | null): void {
    this.approveRequest(invoiceImage);
    this.confirmApproveDialogShown = false;
  }
}
